import { EventsPerspective, NCFileBatchEvent, UseTableColumnsParams } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { isPhoneNative, isWeb } from '@helpers';
import { getRowKey } from '@v2/pages/common/components/NCFileBatchEvents/helpers';
import { ColumnMetadata } from '@components/settings/components/common/animatedTable/types';

export const useTableColumns = ({
  C,
  eventType,
  productionBatchId,
  eventsPerspective,
  shouldDisplayQuantityDelta,
  shouldDisplayVersion,
  shouldShowBarChart,
  shouldDisplayNCFileName,
  shouldDisplayQuantity,
  shouldDisplayBatch,
  shouldDisplayMachine,
  barChartData,
  textConfig,
  orderByConfig,
  orderBy,
  shouldDisplayStatusSubStatusExecutionMode,
  shouldDisplayAccordionTrigger,
  scale = initialScale,
  font = initialFont,
  expandedRows,
  toggleAccordion,
  firstElementKeyValue,
}: UseTableColumnsParams) => {
  if (isPhoneNative) {
    return {
      TABLE_CONFIG: null,
      tableColumns: null,
    };
  }

  // Parse direction from orderBy string (e.g., "StartedOn desc" -> false, "StartedOn asc" -> true)
  const isAscending = orderBy?.toLowerCase()?.endsWith(' asc');

  const TABLE_CONFIG = {
    headerSize: 45,
    lineSize: 40,
    showMoreSize: 63,
    showMoreArrow: false,
    showAllButton: false,
    initialFilter: orderByConfig.start?.[eventType] || 'startedOn',
    initialDirection: isAscending,
  };

  const tertiaryColor = C('Label/Light/Tertiary');

  // Column width configurations by perspective and event type
  const nonTraceEventWidths = {
    [EventsPerspective.BATCH]: {
      timeline: '25%',
      duration: '15%',
      start: '20%',
      version: '10%',
      quantity: '30%',
    },
    [EventsPerspective.NC_FILE]: {
      timeline: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      batch: '15%',
      machine: '15%',
    },
    [EventsPerspective.SINGLE_MACHINE]: {
      timeline: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      batch: '10%',
      ncFile: '20%',
    },
    [EventsPerspective.MULTI_MACHINES]: {
      timeline: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      machine: '10%',
      batch: '15%',
      ncFile: '15%',
    },
  };

  const setupEventWidths = {
    [EventsPerspective.BATCH]: {
      timeline: '35%',
      duration: '15%',
      start: '50%',
    },
    [EventsPerspective.NC_FILE]: {
      timeline: '25%',
      duration: '15%',
      start: '20%',
      batch: '15%',
      machine: '25%',
    },
    [EventsPerspective.SINGLE_MACHINE]: {
      timeline: '25%',
      duration: '15%',
      start: '20%',
      batch: '15%',
      ncFile: '25%',
    },
    [EventsPerspective.MULTI_MACHINES]: {
      statusSubStatus: '35%',
      duration: '15%',
      start: '30%',
      version: '20%',
    },
  };

  const traceEventWidths = {
    [EventsPerspective.BATCH]: {
      statusSubStatus: '35%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      countingMethod: isWeb ? '30%' : '35%',
      startBlock: '20%',
    },
    [EventsPerspective.NC_FILE]: {
      statusSubStatus: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      batch: '10%',
      machine: '15%',
      countingMethod: isWeb ? '30%' : '35%',
      startBlock: '25%',
    },
    [EventsPerspective.SINGLE_MACHINE]: {
      statusSubStatus: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      countingMethod: isWeb ? '30%' : '35%',
      startBlock: '20%',
    },
    [EventsPerspective.MULTI_MACHINES]: {
      statusSubStatus: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      batch: '10%',
      machine: '20%',
    },
  };

  const subRowsEventWidths = {
    [EventsPerspective.BATCH]: {
      statusSubStatus: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      countingMethod: isWeb ? '20%' : '35%',
      startBlock: '20%',
    },
    [EventsPerspective.NC_FILE]: {
      statusSubStatus: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      batch: '10%',
      machine: '15%',
      countingMethod: isWeb ? '20%' : '35%',
      startBlock: '25%',
    },
    [EventsPerspective.SINGLE_MACHINE]: {
      statusSubStatus: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      countingMethod: isWeb ? '20%' : '35%',
      startBlock: '20%',
    },
    [EventsPerspective.MULTI_MACHINES]: {
      statusSubStatus: '25%',
      duration: '15%',
      start: '20%',
      version: '5%',
      quantityDelta: '5%',
      batch: '10%',
      machine: '20%',
    },
  };

  const COLUMN_WIDTHS = {
    [NcFileBatchEventType.CYCLE]: nonTraceEventWidths,
    [NcFileBatchEventType.ACTIVE]: nonTraceEventWidths,
    [NcFileBatchEventType.CHANGE]: nonTraceEventWidths,
    [NcFileBatchEventType.SETUP]: setupEventWidths,
    [NcFileBatchEventType.TRACE]: traceEventWidths,
  };

  // Determine current perspective
  let currentPerspective = EventsPerspective.BATCH;
  if (productionBatchId == null && eventsPerspective) {
    currentPerspective = eventsPerspective;
  } else if (productionBatchId != null) {
    currentPerspective = EventsPerspective.BATCH;
  }

  // Get widths for current event type and perspective
  const widthConfig = COLUMN_WIDTHS[eventType]?.[currentPerspective] || {};

  const startWidth = widthConfig.start || '30%';
  const quantityWidth = widthConfig.quantity || '20%';
  const quantityDeltaWidth = widthConfig.quantityDelta || '5%';
  const batchWidth = widthConfig.batch || '15%';
  const machineWidth = widthConfig.machine || '15%';
  const ncFileWidth = widthConfig.ncFile || '15%';
  const versionWidth = widthConfig.version || '10%';

  const multiOrSingleMachinePerspective =
    !!eventsPerspective && [EventsPerspective.SINGLE_MACHINE, EventsPerspective.MULTI_MACHINES].includes(eventsPerspective);

  // Create column configurations for main rows (metadata-based)
  const createMainRowColumnsMetadata = (): ColumnMetadata[] => [
    ...(shouldDisplayStatusSubStatusExecutionMode
      ? [
          {
            type: 'status',
            title: '',
            orderBy: '',
            sort: false,
            width: widthConfig.statusSubStatus || '35%',
            titleColor: tertiaryColor,
            scrollable: isWeb,
            includeExtraInfoBasedOnNCFileStatus: multiOrSingleMachinePerspective,
          },
        ]
      : []),
    ...(shouldShowBarChart
      ? [
          {
            type: 'barChart',
            // title: () => {
            //   if (!barChartData || !barChartData[0]) return null;
            //   const chartData = barChartData[0];

            //   return (
            //     <View style={{ width: '100%' }}>
            //       <EventBarChartMarkers
            //         idealTimeInSec={chartData.idealTimeInSec}
            //         paidTimeInSec={chartData.paidTimeInSec}
            //         actualTimeInSec={chartData.actualTimeInSec}
            //         scheduledTimeInSec={chartData.scheduledTimeInSec}
            //         maxTimeInSec={chartData.maxTimeInSec}
            //         showMarkers={chartData.showDots}
            //         containerHeight={TABLE_CONFIG.headerSize}
            //         C={C}
            //         scale={scale}
            //       />
            //     </View>
            //   );
            // },
            title: '',
            orderBy: '',
            sort: false,
            width: widthConfig.timeline || '35%',
            titleColor: tertiaryColor,
            // Pass chart config as metadata
            eventType,
            eventsPerspective,
            maxTimeInSec: barChartData?.[0]?.maxTimeInSec,
            idealTimeInSec: barChartData?.[0]?.idealTimeInSec,
            paidTimeInSec: barChartData?.[0]?.paidTimeInSec,
            actualTimeInSec: barChartData?.[0]?.actualTimeInSec,
            scheduledTimeInSec: barChartData?.[0]?.scheduledTimeInSec,
            showDots: barChartData?.[0]?.showDots,
            firstElementKeyValue,
          },
        ]
      : []),
    {
      type: 'duration',
      title: 'Duration',
      orderBy: orderByConfig.duration?.[eventType] ?? '',
      sort: false,
      width: widthConfig.duration || '15%',
      titleColor: tertiaryColor,
      style: { paddingLeft: scale(26) },
      titleStyle: { paddingLeft: scale(26) },
      eventType,
      textConfig,
    },
    {
      type: 'startTime',
      title: 'Start',
      orderBy: orderByConfig.start?.[eventType] ?? '',
      sort: true,
      width: startWidth,
      titleColor: tertiaryColor,
      eventType,
      textConfig,
    },
    ...(shouldDisplayVersion
      ? [
          {
            type: 'version',
            title: 'Version',
            orderBy: '',
            sort: false,
            width: versionWidth,
            titleColor: tertiaryColor,
          },
        ]
      : []),
    ...(shouldDisplayQuantityDelta
      ? [
          {
            type: 'quantityDelta',
            title: '',
            orderBy: '',
            sort: false,
            width: quantityDeltaWidth,
            titleColor: tertiaryColor,
          },
        ]
      : []),
    ...(shouldDisplayQuantity
      ? [
          {
            type: 'quantity',
            title: 'Quantity',
            orderBy: orderByConfig.quantity?.[eventType] ?? '',
            sort: false,
            width: quantityWidth,
            titleColor: tertiaryColor,
          },
        ]
      : []),
    ...(shouldDisplayBatch
      ? [
          {
            type: 'batch',
            title: 'Batch',
            orderBy: orderByConfig.batch?.[eventType] ?? '',
            sort: false,
            width: batchWidth,
            titleColor: tertiaryColor,
            eventType,
            textConfig,
            isMultiOrSingleMachinePerspective: multiOrSingleMachinePerspective,
          },
        ]
      : []),
    ...(shouldDisplayNCFileName
      ? [
          {
            type: 'ncFileName',
            title: 'NC File',
            orderBy: '',
            sort: false,
            width: ncFileWidth,
            titleColor: tertiaryColor,
          },
        ]
      : []),
    ...(shouldDisplayMachine
      ? [
          {
            type: 'machine',
            title: 'Machine',
            orderBy: orderByConfig.machine?.[eventType] ?? '',
            sort: false,
            width: machineWidth,
            titleColor: tertiaryColor,
            eventType,
            textConfig,
          },
        ]
      : []),
    ...(shouldDisplayAccordionTrigger
      ? [
          {
            type: 'accordionTrigger',
            title: '',
            orderBy: '',
            sort: false,
            width: scale(19),
            titleColor: tertiaryColor,
            isExpanded: (item: NCFileBatchEvent) => {
              const rowKey = getRowKey(eventType, item);
              return expandedRows?.[rowKey]?.isExpanded || false;
            },
            getRowKey: (item: NCFileBatchEvent) => getRowKey(eventType, item),
            getRowState: (rowKey: string | number) => expandedRows?.[rowKey] || null,
          },
        ]
      : []),
  ];

  const createSubRowColumnsMetadata = (): ColumnMetadata[] => {
    const widthConfig = subRowsEventWidths?.[eventsPerspective];

    return [
      {
        type: 'subrow-status',
        title: '',
        orderBy: '',
        sort: false,
        width: widthConfig?.statusSubStatus || '35%',
        titleColor: tertiaryColor,
      },
      {
        type: 'subrow-duration',
        title: 'Duration',
        orderBy: '',
        sort: false,
        width: widthConfig?.duration || '15%',
        titleColor: tertiaryColor,
        style: { paddingLeft: scale(26) },
        titleStyle: { paddingLeft: scale(26) },
        textConfig,
      },
      {
        type: 'subrow-start',
        title: 'Start',
        orderBy: '',
        sort: false,
        width: widthConfig?.start || '30%',
        titleColor: tertiaryColor,
        textConfig,
      },
      {
        type: 'subrow-version',
        title: 'Version',
        orderBy: '',
        sort: false,
        width: widthConfig?.version || '5%',
        titleColor: tertiaryColor,
      },
      {
        type: 'subrow-quantityDelta',
        title: '',
        orderBy: '',
        sort: false,
        width: widthConfig?.quantityDelta || '5%',
        titleColor: tertiaryColor,
      },
      {
        type: 'subrow-countingMethod',
        title: '',
        orderBy: '',
        sort: false,
        width: widthConfig?.countingMethod || '15%',
        titleColor: tertiaryColor,
      },
      {
        type: 'subrow-startBlock',
        title: '',
        orderBy: '',
        sort: false,
        width: widthConfig?.startBlock || '15%',
        titleColor: tertiaryColor,
      },
    ];
  };

  const getDynamicColumns = (item: NCFileBatchEvent): ColumnMetadata[] => {
    if (item?.__isSubRowForColumnConfig) {
      return createSubRowColumnsMetadata();
    }
    return createMainRowColumnsMetadata();
  };

  return { tableColumns: getDynamicColumns, TABLE_CONFIG, toggleAccordion };
};
